import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/storybook/components/Button/Button';
import { buildSharedViewDetailRows, formatSharedViewDetailsText } from '@/utils/filter/sharedViewDetailRows';
import type { SharedViewFailureDetails } from '@/utils/filter/sharedViewDiagnostics';
import { TestIds } from '@/components/filter/SharedViewDialog/testIds';

type CopyStatus = 'idle' | 'copied' | 'failed';

export interface SharedViewErrorActionsProps {
  errorDetails?: SharedViewFailureDetails;
  areDetailsVisible: boolean;
  onToggleDetails: () => void;
  onApply: () => void;
}

const SharedViewErrorActions: FC<SharedViewErrorActionsProps> = ({
  errorDetails,
  areDetailsVisible,
  onToggleDetails,
  onApply,
}) => {
  const { t } = useTranslation();
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  const getCopyLabel = () => {
    if (copyStatus === 'copied') return t('global.filters.sharedViewDialog.errorDetails.copied');
    if (copyStatus === 'failed') return t('global.filters.sharedViewDialog.errorDetails.copyFailed');
    return t('global.filters.sharedViewDialog.errorDetails.copyDetails');
  };

  const handleCopy = async () => {
    if (!errorDetails) return;
    try {
      await navigator.clipboard.writeText(formatSharedViewDetailsText(buildSharedViewDetailRows(errorDetails, t)));
      setCopyStatus('copied');
    } catch {
      setCopyStatus('failed');
    }
  };

  return (
    <>
      {errorDetails && (
        <Button variant="outlined" onClick={onToggleDetails} data-testid={TestIds.toggleDetails}>
          {t(`global.filters.sharedViewDialog.errorDetails.${areDetailsVisible ? 'hide' : 'show'}`)}
        </Button>
      )}
      {errorDetails && areDetailsVisible && (
        <Button variant="outlined" onClick={handleCopy} data-testid={TestIds.copyDetails}>
          {getCopyLabel()}
        </Button>
      )}
      <Button variant="primary" onClick={onApply} data-testid={TestIds.loadDefault}>
        {t('global.filters.sharedViewDialog.loadDefaultView')}
      </Button>
    </>
  );
};

export default SharedViewErrorActions;
